import { z } from "zod";

import type { TenantMembershipRole, TenantProductVisibility } from "~/common/types/graphql.generated";
import type { GetTenantQuery, GetTenantsQuery } from "./gqls";

/** Prefix prepended to the tenant shortCode when sending to the API. */
export const TENANT_SHORTCODE_PREFIX = "u-";

/** Create-form shape: name, shortCode (without prefix), and the auto-approve flag. */
export const zTenantFormData = z.object({
  name: z.string().min(1),
  shortCode: z.string().min(1),
  allowAutoApproveChangeRequests: z.boolean(),
});
export const zTenantFormDataKeys = zTenantFormData.keyof();
export type TenantFormData = z.infer<typeof zTenantFormData>;

/** Edit-form shape: shortCode is immutable, so it's dropped and `id` is added. */
export const zTenantEditFormData = zTenantFormData.omit({ shortCode: true }).extend({
  id: z.string(),
});
export const zTenantEditFormDataKeys = zTenantEditFormData.keyof();
export type TenantEditFormData = z.infer<typeof zTenantEditFormData>;

export type TenantRaw = GetTenantsQuery["tenants"][number];
export type TenantDetailRaw = NonNullable<GetTenantQuery["tenantsByPk"]>;

/**
 * Visibility levels of a tenant-product connection. Re-exported from the
 * generated mutation enum; the Hasura table column reads back as a plain
 * string, so raw values are narrowed in the transformer.
 */
export type { TenantProductVisibility } from "~/common/types/graphql.generated";
export const TENANT_PRODUCT_VISIBILITIES = [
  "private",
  "organization",
  "authenticated",
] as const satisfies readonly TenantProductVisibility[];

/** Legacy manager values must remain readable when editable roles change. */
export const READABLE_TENANT_MEMBERSHIP_ROLES = [
  "member",
  "manager",
] as const satisfies readonly TenantMembershipRole[];

/** Keep the existing choices until the coordinated organization migration cutover. */
export const EDITABLE_TENANT_MEMBERSHIP_ROLES = [
  "member",
  "manager",
] as const satisfies readonly TenantMembershipRole[];
export type EditableTenantMembershipRole = (typeof EDITABLE_TENANT_MEMBERSHIP_ROLES)[number];

/** An installed product connection with its visibility. */
export interface TenantProductConnection {
  id: string;
  productId: string;
  productName: string;
  productDeactivatedAt: string | null;
  visibility: TenantProductVisibility;
}

/** UI-ready row passed from the container to the table. */
export interface TenantRow {
  id: string;
  name: string;
  shortCode: string;
  organization: {
    id: string;
    name: string;
    shortCode: string;
  };
  /** Owner's display name (or email), null when the owner is not set. */
  ownerLabel: string | null;
  updatedAt: string;
  isDeactivated: boolean;
  /** Products already installed on this tenant (bulk install skips these). */
  installedProductIds: string[];
}

/** A tenant member resolved from `userTenants`. */
export interface TenantMember {
  id: string;
  displayName: string | null;
  email: string;
  deactivatedAt: string | null;
  role: TenantMembershipRole;
}

/** The tenant owner resolved from `ownerUser`. */
export interface TenantOwner {
  id: string;
  displayName: string | null;
  email: string;
  deactivatedAt: string | null;
}

/** Fields shown on the read-only detail view. */
export interface TenantDetailUI {
  id: string;
  name: string;
  shortCode: string;
  organizationId: string;
  organization: {
    id: string;
    name: string;
    shortCode: string;
  };
  allowAutoApproveChangeRequests: boolean;
  isDeactivated: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  deactivatedAt: string | null;
  owner: TenantOwner | null;
  members: TenantMember[];
  productConnections: TenantProductConnection[];
}

/** Lightweight user option used by the member picker and the user filter. */
export interface FilterUserOption {
  id: string;
  displayName: string | null;
  email: string;
  deactivatedAt: string | null;
}
